import istor from '../Assets/istor2.png';
import ministor from '../Assets/ministor.png';
import {useCallback, useEffect, useMemo, useState} from "react";
import '@monolithedev/ui';
import { UserAvatarUpload } from './OrganizationImageEditor.jsx';
import { useMediaQuery } from '../useMediaQuery';
import { obtenirInitiales, obtenirCouleurAvatar } from '../avatar';
import { EyeOff, EyeOn } from '../PasswordEye';

// Bouton d'édition d'image (identique à celui de la page établissement)
const EditImageButtonIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.8442 18C1.33297 18 0.897845 17.8204 0.538815 17.4612C0.179605 17.1022 0 16.667 0 16.1558V1.8442C0 1.33297 0.179605 0.897845 0.538815 0.538815C0.897845 0.179605 1.33378 0 1.84663 0H9.93941V1.51366H1.84663C1.74944 1.51366 1.66964 1.54486 1.60725 1.60725C1.54486 1.66964 1.51366 1.74944 1.51366 1.84663V16.1534C1.51366 16.2506 1.54486 16.3304 1.60725 16.3928C1.66964 16.4551 1.74944 16.4863 1.84663 16.4863H16.1534C16.2506 16.4863 16.3304 16.4551 16.3928 16.3928C16.4551 16.3304 16.4863 16.2506 16.4863 16.1534V8.06059H18V16.1534C18 16.6662 17.8204 17.1022 17.4612 17.4612C17.1022 17.8204 16.667 18 16.1558 18H1.8442ZM3.09088 14.0114H14.9143L11.2472 9.12902L8.0449 13.277L5.78956 10.4409L3.09088 14.0114ZM14.3443 5.79794V3.65567H12.2021V2.14201H14.3443V0H15.858V2.14201H18V3.65567H15.858V5.79794H14.3443Z" fill="#173C50"/>
    </svg>
);

const editImageButtonStyle = (hovered, active, visible) => ({
    width: '36px',
    height: '36px',
    borderRadius: '18px',
    border: `1px solid ${active ? '#206095' : hovered ? '#007BD3' : '#E0E0E0'}`,
    backgroundColor: active ? 'color-mix(in srgb, #DEE5EE 80%, transparent)' : hovered ? 'color-mix(in srgb, #E9EDF2 80%, transparent)' : 'color-mix(in srgb, #F4F4F7 80%, transparent)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    cursor: 'pointer',
    opacity: visible ? 1 : 0,
    pointerEvents: visible ? 'auto' : 'none',
    transition: 'opacity 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
});

// Icône établissement (SVG rose propre au login), en markup brut, passée à <istor-select> (option.icon).
// Le clipPath d'origine découpait sur toute la surface (22x22) : sans effet visuel, retiré ici.
const ETAB_ICON_SVG = `<svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 22C17.0751 22 22 17.0751 22 11C22 4.92487 17.0751 0 11 0C4.92487 0 0 4.92487 0 11C0 17.0751 4.92487 22 11 22Z" fill="white"/>
    <path d="M11 21C16.5228 21 21 16.5228 21 11C21 5.47715 16.5228 1 11 1C5.47715 1 1 5.47715 1 11C1 16.5228 5.47715 21 11 21Z" fill="#F21B5C"/>
    <path d="M15.7861 6.83301H6.21289L10.999 4.55273L15.7861 6.83301Z" stroke="white" stroke-miterlimit="16"/>
    <path d="M6.31641 8.05176V13.5088" stroke="white" stroke-linecap="round"/>
    <path d="M8.63086 8.05176V13.5088" stroke="white" stroke-linecap="round"/>
    <path d="M10.9473 8.05176V13.5088" stroke="white" stroke-linecap="round"/>
    <path d="M13.2632 8.05176V13.5088" stroke="white" stroke-linecap="round"/>
    <path d="M15.5776 8.05176V13.5088" stroke="white" stroke-linecap="round"/>
    <path d="M4.99512 14.583H16.7359" stroke="white" stroke-linecap="round"/>
    <path d="M4.33252 15.75H17.3962" stroke="white" stroke-linecap="round"/>
</svg>`;

const Login = () => {
    const API_BASE_URL = 'https://dev-auth.apps.istor.io';
    const CONTENTS = {
        OK: {
            title: '“Oh, bonjour !',
            message: 'Je m’appelle Istor. Pour vous servir.\n' +
                'Soyez les bienvenus sur cet outil de\n' +
                'conception de médiation culturelle en ligne !\n' +
                'Je vous laisse vous connecter sur \n' +
                'votre compte utilisateur.”',
            short: 'Soyez les bienvenus ! Je vous laisse vous\nconnecter à votre compte utilisateur.”'
        },
        ERROR: {
            title: '“Mince...',
            message: 'Il semble que vous vous soyez trompé dans \n' +
                'vos informations de connexion.\n' +
                'Peut-être pourriez vous réessayer ?”',
            short: 'Il semble que vous vous soyez trompé dans vos informations de connexion.\nPeut-être pourriez vous réessayer ?”'
        },
        RESET: {
            title: '“Re-bonjour !...',
            message: 'Vous pouvez entrer vos nouveaux identifiants\n' +
                'et ouvrir votre interface personnelle Istor.io.\n' +
                'À tout de suite !”',
            short: 'Vous pouvez entrer vos nouveaux identifiants et ouvrir votre interface personnelle Istor.io.\nÀ tout de suite !”'
        },
        SELECT: {
            title: '“C’est parti !...',
            message: 'Par quoi souhaitez-vous commencer ?“'
        }
    }
    const [showPassword, setShowPassword] = useState(false);
    const isMobile = useMediaQuery('(max-width: 750px)');

    const [error, setError] = useState(null);
    const [resetSuccess] = useState(() => new URLSearchParams(window.location.search).get('reset') === 'success');
    const [istorContent, setIstorContent] = useState(resetSuccess ? CONTENTS['RESET'] : CONTENTS['OK']);
    const [steps, setSteps] = useState(0);
    const [organisations, setOrganisations] = useState([]);
    useEffect(() => {
        if (steps === 1 && organisations?.length) {
            setSelectedInst(organisations[0]);
        }
    }, [steps, organisations]);
    useEffect(() => {
        if (error) {
            setIstorContent(CONTENTS['ERROR']);
        } else if (resetSuccess) {
            setIstorContent(CONTENTS['RESET']);
        } else {
            setIstorContent(CONTENTS['OK']);
        }
    }, [error]);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [profile, setProfile] = useState(null);

    const fetchProfile = async (token) => {
        try {
            const res = await fetch(`${API_BASE_URL}/user/profile`, {
                headers: {Authorization: `Bearer ${token}`},
            });
            if (res.ok) setProfile(await res.json());
        } catch {
            // avatar affichera '?' faute de profil
        }
    };

    useEffect(() => {
        if (steps === 0) {
            if (error) {
                setIstorContent(CONTENTS['ERROR'])
            } else if (resetSuccess) {
                setIstorContent(CONTENTS['RESET'])
            } else {
                setIstorContent(CONTENTS['OK'])
            }
        } else {
            setIstorContent(CONTENTS['SELECT'])
        }
    }, [steps]);

    const fetchLogin = async (email, password, checked) => {
        setError(null);

        try {
            const response = await fetch(`${API_BASE_URL}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({email, password}),
            });

            if (!response.ok) {
                setError("Veuillez réessayer s'il vous plait !");
                return;
            }

            const data = await response.json();
            const accessToken = data?.accessToken;
            const refreshToken = data?.refreshToken;

            if (!accessToken) {
                setError("Veuillez réessayer s'il vous plait !");
                return;
            }


            const isHttps = window.location.protocol === 'https:';
            const secureAttr = isHttps ? ';Secure' : '';

            // Exemple : cookie valable 1 heures
            const maxAgeSeconds = 1 * 60 * 60;
            const maxAgeSecondsRefresh = 60 * 60 * 24;


            document.cookie =
                `jwt=${encodeURIComponent(accessToken)}` +
                `;Domain=.istor.io` +
                `;Path=/` +
                `;Max-Age=${maxAgeSeconds}` +
                `;SameSite=None` +
                `;Secure`;

            document.cookie =
                `refreshJwt=${encodeURIComponent(refreshToken)}` +
                `;Domain=.istor.io` +
                `;Path=/` +
                `;Max-Age=${maxAgeSecondsRefresh}` +
                `;SameSite=None` +
                `;Secure`;
            //local
            // document.cookie =
            //     `jwt=${encodeURIComponent(accessToken)}` +
            //     `;Max-Age=${maxAgeSeconds}` +
            //     `;SameSite=Lax`;
            // document.cookie =
            //     `refreshJwt=${encodeURIComponent(refreshToken)}` +
            //     `;Max-Age=${maxAgeSecondsRefresh}` +
            //     `;SameSite=Lax`;

            const listOrganisationsResponse = await fetch(`${API_BASE_URL}/autorisation/user/role/organisation`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${encodeURIComponent(accessToken)}`,
                }
            });

            if (!listOrganisationsResponse.ok) {
                setError("Veuillez réessayer s'il vous plait !");
                return;
            }

            const organisationsData = await listOrganisationsResponse.json();

            if (!organisationsData?.length) {
                window.location.href = '/no-org';
                return;
            }
            if (organisationsData.length === 1) {
                window.location.href = '/' + organisationsData[0]?.organization?.instanceName;
                return;
            }

            fetchProfile(accessToken);
            setOrganisations(organisationsData || []);
            setSteps(1);


        } catch (err) {
            console.error('Erreur lors du login :', err);
            setError("Veuillez réessayer s'il vous plait !");
        }
    };

    const handleLogin = (e) => {
        //e.target[0/1].value
        //e.target[2].checked
        fetchLogin(email, password, e.target[2].checked);
    }

    const [selectedInst, setSelectedInst] = useState(null)

    // Options du sélecteur d'établissement pour <istor-select> (icône rose propre au login).
    const etabOptions = useMemo(
        () => (organisations ?? []).map((o) => ({
            value: o?.organization?.instanceName,
            label: o?.organization?.name,
            icon: ETAB_ICON_SVG,
        })),
        [organisations]
    );

    // Branche les options (propriété objet) + l'événement `change` sur le Web Component.
    // React 19 : via une ref, avec cleanup retourné (supprime le listener au démontage).
    const bindSelect = useCallback((el) => {
        if (!el) return;
        el.options = etabOptions;
        const onChange = (e) => {
            const inst = (organisations ?? []).find(
                (o) => o?.organization?.instanceName === e.detail?.value
            );
            if (inst) setSelectedInst(inst);
        };
        el.addEventListener('change', onChange);
        return () => el.removeEventListener('change', onChange);
    }, [etabOptions, organisations]);

    const [avatarHovered, setAvatarHovered] = useState(false);
    const [hoveredAvatarEditBtn, setHoveredAvatarEditBtn] = useState(false);
    const [activeAvatarEditBtn, setActiveAvatarEditBtn] = useState(false);

    useEffect(() => {
        if (steps === 1) {
            setSelectedInst(organisations[0])
        } else {
            setSelectedInst(null)
        }
    }, [steps]);

    const fetchLoginWithToken = async (token) => {
        setError(null);
        try {
            if (!token) {
                resetLogin();
                return;
            }

            // décode base64url
            const payloadBase64Url = token.split('.')[1];
            const payloadJson = atob(payloadBase64Url.replace(/-/g, '+').replace(/_/g, '/'));
            const payload = JSON.parse(payloadJson);

            const now = Math.floor(Date.now() / 1000);
            if (payload?.exp && payload.exp < now) {
                resetLogin();
                setError("Veuillez vous reconnecter s'il vous plait !");
                return;
            }

            const listOrganisationsResponse = await fetch(`${API_BASE_URL}/autorisation/user/role/organisation`, {
                method: 'GET',
                headers: {Authorization: `Bearer ${token}`},
            });

            if (!listOrganisationsResponse.ok) {
                resetLogin();
                setError("Veuillez vous reconnecter s'il vous plait !");
                return;
            }

            const organisationsData = await listOrganisationsResponse.json();

            if (!organisationsData?.length) {
                window.location.href = '/no-org';
                return;
            }
            if (organisationsData.length === 1) {
                window.location.href = '/' + organisationsData[0]?.organization?.instanceName;
                return;
            }

            fetchProfile(token);
            setOrganisations(organisationsData || []);
            setSteps(1);
        } catch (err) {
            console.error('Erreur lors du login :', err);
            resetLogin();
            setError("Veuillez vous reconnecter s'il vous plait !");
        }
    };

    const resetLogin = () => {
        setSteps(0);
        setOrganisations([]);
        setSelectedInst(null);
        setProfile(null);
        setError(null);
        setIstorContent(resetSuccess ? CONTENTS['RESET'] : CONTENTS['OK']);
    };

    const handleLogout = () => {
        document.cookie = 'jwt=;Domain=.istor.io;Path=/;Max-Age=0;SameSite=None;Secure';
        document.cookie = 'refreshJwt=;Domain=.istor.io;Path=/;Max-Age=0;SameSite=None;Secure';
        try { localStorage.setItem('logout', String(Date.now())); } catch {}
        resetLogin();
    };

    useEffect(() => {
        const getCookie = (name) => {
            const m = document.cookie.split('; ').find((r) => r.startsWith(`${name}=`));
            return m ? m.split('=')[1] : undefined;
        };
        const jwt = getCookie('jwt');
        if (jwt) fetchLoginWithToken(jwt);
        else resetLogin();
    }, []);

    useEffect(() => {
        const getCookie = (name) => {
            const m = document.cookie.split('; ').find((r) => r.startsWith(`${name}=`));
            return m ? m.split('=')[1] : undefined;
        };
        const handlePossibleLogout = () => {
            if (!getCookie('jwt')) resetLogin();
        };
        const onStorage = (e) => {
            if (e.key === 'logout') handlePossibleLogout();
        };
        window.addEventListener('storage', onStorage);
        window.addEventListener('focus', handlePossibleLogout);
        return () => {
            window.removeEventListener('storage', onStorage);
            window.removeEventListener('focus', handlePossibleLogout);
        };
    }, []);


    const nomComplet = [profile?.prenom, profile?.nom].filter(Boolean).join(' ');

    // Champs du formulaire de connexion, partagés entre les rendus desktop et mobile.
    const errorBanner = (
        <div style={{
            width: '100%',
            height: '36px',
            background: error ? '#FFEDED' : '#D0F0CD',
            borderRadius: '10px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: error ? '#F46969' : '#44A434',
            fontSize: '13px',
            fontWeight: '400',
            opacity: (error || resetSuccess) ? 1 : 0,
            transition: 'opacity 0.1s ease',
            flexShrink: 0,
        }}>{error || (resetSuccess ? 'Mot de passe réinitialisé avec succès' : '')}</div>
    );

    const emailInput = (
        <input id="emaili" style={{
            width: '100%',
            height: '36px',
            borderRadius: '10px',
            padding: '0 15px',
            fontSize: '13px',
            fontWeight: '400',
            backgroundColor: '#FFFFFF',
        }} type="email"
               onChange={(e) => setEmail(e.target.value)}
               value={email}
               required={true}
               placeholder="Entrez votre mail"/>
    );

    const passwordField = (
        <div style={{ position: 'relative', width: '100%' }}>
            <button
                type="button"
                className="eye-toggle"
                onClick={() => setShowPassword(p => !p)}
                style={{
                    position: 'absolute',
                    top: '50%',
                    right: '8px',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    width: '20px',
                    height: '20px',
                }}
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            >
                {showPassword ? <EyeOff/> : <EyeOn/>}
            </button>
            <input id="passwordi" style={{
                width: '100%',
                height: '36px',
                borderRadius: '10px',
                padding: '0 15px',
                fontSize: '13px',
                fontWeight: '400',
                backgroundColor: '#FFFFFF',
                paddingRight: '36px',
            }} type={showPassword ? 'text' : 'password'}
                   autoComplete="current-password"
                   onChange={(e) => setPassword(e.target.value)}
                   value={password}
                   required={true}
                   placeholder="Entrez votre mot de passe"/>
        </div>
    );

    const mdpOublieLink = (
        <a href="/forgot-password" className="mdp-oublie" style={{
            fontSize: '10px',
            fontWeight: '400',
        }}>mot de passe oublié</a>
    );

    const connexionButton = (
        <button className="connexion" type="submit" style={{
            width: '100%',
            height: '36px',
            borderRadius: '10px',
            color: '#FFFFFF',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
        }}>Connexion</button>
    );

    const inscrivezLink = (
        <a href="/register" className="change" style={{
            width: '100%',
            height: '36px',
            borderRadius: '10px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
        }}>Inscrivez-vous</a>
    );

    if (steps === 0 && isMobile) {
        return (
            <form
                onSubmit={(e) => { e.preventDefault(); handleLogin(e); }}
                style={{
                    width: '330px',
                    height: '517px', maxHeight: 'calc(100dvh - 40px)',
                    boxSizing: 'border-box',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '20px',
                    boxShadow: '0px 10px 40px rgba(0, 0, 0, 0.1)',
                    padding: '20px 30px 30px 30px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                }}
            >
                {/* Haut : mini-mascotte + titre + texte court, aligné à gauche. Bascule OK/erreur. */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                        <img src={ministor} alt="istor" style={{ width: '60px', height: '60px', borderRadius: '50%', flexShrink: 0 }}/>
                        <div style={{ fontSize: '24px', fontWeight: '600', color: '#173C50' }}>{(error ? CONTENTS['ERROR'] : resetSuccess ? CONTENTS['RESET'] : CONTENTS['OK']).title}</div>
                    </div>
                    <div style={{
                        marginTop: '10px',
                        whiteSpace: 'pre-line',
                        fontSize: '13px',
                        fontWeight: '400',
                        color: '#173C50',
                        lineHeight: '19px',
                        textAlign: 'left',
                    }}>{(error ? CONTENTS['ERROR'] : resetSuccess ? CONTENTS['RESET'] : CONTENTS['OK']).short}</div>
                </div>

                {/* Bas : formulaire, centré */}
                <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {errorBanner}
                    <div style={{ marginTop: '14px' }}>{emailInput}</div>
                    <div style={{ marginTop: '14px' }}>{passwordField}</div>
                    <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end' }}>{mdpOublieLink}</div>
                    <div style={{ marginTop: '10px' }}>{connexionButton}</div>
                    <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'center', fontWeight: '600', fontSize: '13px', color: '#49494A' }}>
                        Vous n’avez pas votre compte ?
                    </div>
                    <div style={{ marginTop: '10px' }}>{inscrivezLink}</div>
                </div>
            </form>
        );
    }

    // Pièces de la vue de sélection d'établissement (étape 1), partagées desktop + mobile.
    const avatarUpload = (
        <UserAvatarUpload onUploaded={(u) => setProfile(prev => ({ ...(prev ?? {}), ...u }))}>
            {({ trigger }) => (
                <div
                    onClick={trigger}
                    onMouseEnter={() => setAvatarHovered(true)}
                    onMouseLeave={() => setAvatarHovered(false)}
                    style={{ position: 'relative', width: '60px', height: '60px', cursor: 'pointer' }}
                >
                    {profile?.avatarUrl ? (
                        <img src={profile.avatarUrl} alt="" style={{
                            width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover',
                            border: '2px solid #FFFFFF', boxSizing: 'border-box', display: 'block',
                        }}/>
                    ) : (
                        <div style={{
                            width: '60px', height: '60px', borderRadius: '50%', border: '2px solid #FFFFFF',
                            boxSizing: 'border-box', backgroundColor: obtenirCouleurAvatar(nomComplet),
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#FFFFFF', fontSize: '28px', fontWeight: '400',
                        }}>{obtenirInitiales(nomComplet)}</div>
                    )}
                    <button
                        onClick={(e) => { e.stopPropagation(); trigger(); }}
                        onMouseEnter={() => setHoveredAvatarEditBtn(true)}
                        onMouseLeave={() => { setHoveredAvatarEditBtn(false); setActiveAvatarEditBtn(false); }}
                        onMouseDown={() => setActiveAvatarEditBtn(true)}
                        onMouseUp={() => setActiveAvatarEditBtn(false)}
                        aria-label="Changer la photo de profil"
                        style={{
                            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                            ...editImageButtonStyle(hoveredAvatarEditBtn, activeAvatarEditBtn, avatarHovered),
                        }}
                    >
                        <EditImageButtonIcon />
                    </button>
                </div>
            )}
        </UserAvatarUpload>
    );

    const nameSpan = (
        <span style={{ fontSize: '13px', fontWeight: '600', color: '#173C50', textAlign: 'center' }}>{nomComplet}</span>
    );

    const dropdownBlock = (
        <istor-select
            ref={bindSelect}
            value={selectedInst?.organization?.instanceName ?? ''}
            search-threshold="10"
            max-visible="4"
            search-placeholder="Rechercher un établissement"
            style={{ display: 'block', marginBottom: '20px' }}
        />
    );

    const accederButton = (
        <>
            <button className="connexion" type="button" onClick={() => {
                const inst = selectedInst?.organization?.instanceName;
                if (inst) window.location.href = "/" + inst;
            }} style={{
                width: '100%', height: '36px', borderRadius: '10px', color: '#FFFFFF',
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                fontSize: '13px', fontWeight: '600', cursor: 'pointer',
            }}>Accéder</button>
            <button className="deconnexion" type="button" onClick={handleLogout} style={{
                width: '100%', height: '36px', borderRadius: '10px', color: '#FFFFFF',
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginTop: '10px',
            }}>Déconnexion</button>
        </>
    );

    if (steps === 1 && isMobile) {
        return (
            <div style={{
                width: '330px',
                height: '517px', maxHeight: 'calc(100dvh - 40px)',
                boxSizing: 'border-box',
                backgroundColor: '#FFFFFF',
                borderRadius: '20px',
                boxShadow: '0px 10px 40px rgba(0, 0, 0, 0.1)',
                padding: '20px 30px 30px 30px',
                display: 'flex',
                flexDirection: 'column',
            }}>
                {/* Haut : mini-mascotte + titre + texte court, aligné à gauche */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                        <img src={ministor} alt="istor" style={{ width: '60px', height: '60px', borderRadius: '50%', flexShrink: 0 }}/>
                        <div style={{ fontSize: '24px', fontWeight: '600', color: '#173C50' }}>{'“C’est parti !'}</div>
                    </div>
                    <div style={{ marginTop: '10px', fontSize: '13px', fontWeight: '400', color: '#173C50', lineHeight: '19px', textAlign: 'left' }}>{'Par quoi souhaitez-vous commencer ?“'}</div>
                </div>

                {/* Bas : 60 → pp → 8 → nom → 78 → label → 10 → dropdown → 20 → Accéder → 10 → Déconnexion */}
                <div style={{ marginTop: '60px', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                        {avatarUpload}
                        {nameSpan}
                    </div>
                    <label htmlFor="selecter" style={{
                        marginTop: '78px',
                        marginBottom: '10px',
                        fontSize: '11px',
                        fontWeight: '300',
                        color: '#173C50',
                        display: 'block',
                        textAlign: 'center',
                    }}>Sélectionnez votre établissement :</label>
                    {dropdownBlock}
                    {accederButton}
                </div>
            </div>
        );
    }

    return <div style={{
        width: '700px',
        height: '450px',
        display: 'flex',
        overflow: 'hidden',
        borderRadius: '20px',
        boxShadow: '0px 10px 40px rgba(0, 0, 0, 0.1)',
    }}>
        <div style={{
            height: '100%',
            width: '360px',
            backgroundColor: steps === 0 ? '#173C50' : '#F8F9FA',
            position: 'relative',
            color: '#FFFFFF',
        }}>
            {
                steps === 0 ? <>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '15px',
                        paddingLeft: '30px',
                        left: '0',
                        position: 'absolute',
                        top: error ? '74px' : '36px',
                        transition: 'top 0.1s ease',
                    }}>
                        <div style={{
                            fontSize: '24px',
                            fontWeight: '600',
                        }}>
                            {istorContent.title}
                        </div>
                        <div style={{
                            whiteSpace: 'pre-line',
                            fontSize: '13px',
                            fontWeight: '400',
                            lineHeight: '19px',
                        }}>
                            {istorContent.message}
                        </div>
                    </div>
                    <img style={{
                        position: 'absolute',
                        bottom: 0,
                        right: '-20px',
                        width: '325px',
                        height: '260px',
                    }} src={istor} alt="istor"/>
                </> : <>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '15px',
                        paddingLeft: '30px',
                        left: '0',
                        position: 'absolute',
                        top: '74px',
                        transition: 'top 0.1s ease',
                        color: '#173C50',
                    }}>
                        <div style={{
                            fontSize: '24px',
                            fontWeight: '600',
                        }}>
                            {istorContent.title}
                        </div>
                        <div style={{
                            whiteSpace: 'pre-line',
                            fontSize: '13px',
                            fontWeight: '400',
                            lineHeight: '19px',
                        }}>
                            {istorContent.message}
                        </div>
                    </div>
                    <img style={{
                        width: '36px',
                        height: '36px',
                        marginTop: '20px',
                        marginLeft: '30px',
                        marginBottom: '18px'
                    }} src={ministor} alt="mini istor"/>
                </>
            }
        </div>
        {
            steps === 0 ? <>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleLogin(e);
                        }}
                        style={{
                            width: '340px',
                            height: '100%',
                            backgroundColor: '#FFFFFF',
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '60px 35px 80px 35px',
                            alignItems: 'center',
                            gap: '30px',
                        }}>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '20px',
                            width: '100%',
                        }}>
                            <div style={{
                                width: '100%',
                                height: '36px',
                                background: error ? '#FFEDED' : '#D0F0CD',
                                borderRadius: '10px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                color: error ? '#F46969' : '#44A434',
                                fontSize: '13px',
                                fontWeight: '400',
                                opacity: (error || resetSuccess) ? 1 : 0,
                                transition: 'opacity 0.1s ease',
                            }}>{error || (resetSuccess ? 'Mot de passe r\u00e9initialis\u00e9 avec succ\u00e8s' : '')}</div>
                            <div style={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '14px',
                            }}>
                                <input id="emaili" style={{
                                    width: '100%',
                                    height: '36px',
                                    borderRadius: '10px',
                                    padding: '0 15px',
                                    fontSize: '13px',
                                    fontWeight: '400',
                                    backgroundColor: '#FFFFFF',
                                    zIndex: 2,
                                }} type="email"
                                       onChange={(e) => setEmail(e.target.value)}
                                       value={email}
                                       required={true}
                                       placeholder="Entrez votre mail"/>
                                <div style={{
                                    position: 'relative',
                                }}>
                                    <button
                                        type="button"
                                        className="eye-toggle"
                                        onClick={() => setShowPassword(p => !p)}
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            right: '8px',
                                            transform: 'translateY(-50%)',
                                            cursor: 'pointer',
                                            background: 'none',
                                            border: 'none',
                                            padding: 0,
                                            width: '20px',
                                            height: '20px',
                                        }}
                                        aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                                    >
                                        {showPassword ? <EyeOff/> : <EyeOn/>}
                                    </button>

                                    <input id="passwordi" style={{
                                        width: '100%',
                                        height: '36px',
                                        borderRadius: '10px',
                                        padding: '0 15px',
                                        fontSize: '13px',
                                        fontWeight: '400',
                                        backgroundColor: '#FFFFFF',
                                        paddingRight: '36px',
                                    }} type={showPassword ? 'text' : 'password'}
                                           autoComplete="current-password"
                                           onChange={(e) => setPassword(e.target.value)}
                                           value={password}
                                           required={true}
                                           placeholder="Entrez votre mot de passe"/>
                                </div>
                            </div>
                            <div style={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}>
                                <a href="/forgot-password" className="mdp-oublie" style={{
                                    fontSize: '10px',
                                    fontWeight: '400',
                                }}>mot de passe oublié</a>
                            </div>

                            {/*<div style={{*/}
                            {/*    width: '100%',*/}
                            {/*    display: 'flex',*/}
                            {/*    justifyContent: 'space-between',*/}
                            {/*    alignItems: 'center',*/}
                            {/*}}>*/}
                            {/*    <div*/}
                            {/*        style={{*/}
                            {/*            display: 'flex',*/}
                            {/*            alignItems: 'center',*/}
                            {/*            fontSize: '10px',*/}
                            {/*            color: '#7C7C7D',*/}
                            {/*            fontWeight: '500',*/}
                            {/*            cursor: 'pointer',*/}
                            {/*        }}>*/}
                            {/*        <input style={{*/}
                            {/*            cursor: 'pointer',*/}
                            {/*            width: '12px',*/}
                            {/*            height: '12px',*/}
                            {/*            accentColor: '#007BD3',*/}
                            {/*        }} id="checkbox" type="checkbox"></input>*/}
                            {/*        <label style={{*/}
                            {/*            cursor: 'pointer',*/}
                            {/*            paddingLeft: '8px',*/}
                            {/*        }} htmlFor="checkbox">*/}
                            {/*            Se souvenir de moi*/}
                            {/*        </label>*/}
                            {/*    </div>*/}
                            {/*</div>*/}
                        </div>
                        <button className="connexion" type="submit" style={{
                            width: '100%',
                            height: '36px',
                            borderRadius: '10px',
                            color: '#FFFFFF',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer',
                        }}>Connexion
                        </button>
                        <div style={{
                            width: '100%',
                            marginTop: '29px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '14px',
                            fontWeight: '600',
                            fontSize: '13px',
                            color: '#49494A',
                        }}>
                            Vous n’avez pas votre compte ?
                            <a href="/register" className="change" style={{
                                width: '100%',
                                height: '36px',
                                borderRadius: '10px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontSize: '13px',
                                fontWeight: '600',
                                cursor: 'pointer',
                            }}>Inscrivez-vous</a>
                        </div>
                    </form>
                </> :
                <div style={{
                    width: '340px',
                    padding: '80px 35px 75px 35px',
                    backgroundColor: '#FFFFFF',
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '52px',
                    }}>
                        {avatarUpload}
                        {nameSpan}
                    </div>
                    <label htmlFor="selecter" style={{
                        fontSize: '11px',
                        fontWeight: '300',
                        color: '#173C50',
                        marginBottom: '10px',
                        display: 'block',
                        textAlign: 'center',
                    }}>Sélectionnez votre établissement :</label>
                    {dropdownBlock}
                    {accederButton}
                </div>
        }
    </div>
}

export default Login;